import { createContext, useContext, useMemo, useState } from 'react';

/**
 * The card-to-page zoom needs the same offsets on both sides of a navigation:
 * the home page pushes toward the clicked card as it exits, and the detail page
 * grows out of that same spot as it enters. This holds those offsets across the
 * route change.
 */
const ZoomTransitionContext = createContext(null);

const DEFAULT_OFFSET = { x: 0, y: 200 };

export const SCROLL_KEY = 'homeScrollY';

/**
 * The pages scale past the viewport mid-zoom; locking overflow keeps that from
 * briefly showing scrollbars.
 */
export const beginTransition = () => {
  document.body.classList.add('is-transitioning');
};

export const endTransition = () => {
  document.body.classList.remove('is-transitioning');
};

export const ZoomTransitionProvider = ({ children }) => {
  const [offset, setOffset] = useState(DEFAULT_OFFSET);

  const value = useMemo(() => ({
    offset,
    /** Derive the offset from a card's position in the grid (2 columns on desktop, 1 on mobile). */
    setOffsetFromCard: (index) => {
      const columnsPerRow = window.innerWidth <= 768 ? 1 : 2;
      const row = Math.floor(index / columnsPerRow) + 1;

      // Rows past the second reuse row two's offset so far-down cards don't fly off-screen.
      const y = row <= 2 ? (row * 200) - 200 : 200;
      const x = columnsPerRow === 2 ? (index % columnsPerRow === 0 ? 300 : -300) : 0;

      setOffset({ x, y });
    },
  }), [offset]);

  return (
    <ZoomTransitionContext.Provider value={value}>
      {children}
    </ZoomTransitionContext.Provider>
  );
};

export const useZoomTransition = () => useContext(ZoomTransitionContext);
